import { createAnatomicScrollToolClass } from "../tools/AnatomicScrollTool";
import { createBumpContourToolClass } from "../tools/BumpContourTool";
import { createSegmentationLassoToolClass } from "../tools/SegmentationLassoTool";
import { createMeasureClickToolClass } from "../tools/MeasureClickTool";
import { createPlaneReferenceLineToolClasses } from "../tools/planeReferenceLineTools";
import { createRegionGrowToolClass } from "../tools/RegionGrowTool";
import { recordSignCorrectionApplied } from "./signCorrectionTelemetry";
import { logger } from "@/utils/logger";
import { isProgressiveEnabled } from "./progressiveConfig";
import { perfMeasure } from "./performanceTiming";
import { normalizeVoiLutMetadata } from "./dicomVoi";

// Dynamic imports for Cornerstone3D modules - loaded lazily to avoid WebGL initialization at startup
// NOTE: VTK.js import hack removed - Cornerstone3D 4.x uses VTK.js 34.x which properly handles
// module initialization in production builds (the v30.x bug that caused SURFACE/FAST_LINEAR
// undefined errors has been fixed)
// These are loaded when initializeCornerstone() is first called
let cornerstone: typeof import("@cornerstonejs/core") | null = null;
let cornerstoneTools: typeof import("@cornerstonejs/tools") | null = null;
let cornerstoneDICOMImageLoader: typeof import("@cornerstonejs/dicom-image-loader").default | null =
  null;

let isInitialized = false;
let initializationPromise: Promise<void> | null = null;

// Note: OpenJPEG codec logs verbose [INFO] messages during j2k decoding.
// Use browser dev tools filter (-[INFO]) to suppress if needed.
// Previous console monkey-patching was removed as it's a global hack.

// Transfer Syntax UIDs whose decoders may return Uint16Array even when the
// source DICOM declares PixelRepresentation=1 (signed). For these we two's-
// complement-reinterpret the buffer as Int16Array to recover negative HU values.
export const SIGN_CORRECTION_TRANSFER_SYNTAXES = [
  "1.2.840.10008.1.2.4.57", // JPEG Lossless, Non-Hierarchical (Process 14)
  "1.2.840.10008.1.2.4.70", // JPEG Lossless, Non-Hierarchical, First-Order Prediction
  "1.2.840.10008.1.2.4.90", // JPEG 2000 Lossless
  "1.2.840.10008.1.2.4.91", // JPEG 2000 Lossy
  "1.2.840.10008.1.2.4.201", // HTJ2K Lossless
  "1.2.840.10008.1.2.4.202", // HTJ2K Lossless with RPCL Options (our canonical bytes)
  "1.2.840.10008.1.2.4.203", // HTJ2K (lossy)
];

/**
 * Cornerstone3D cache size in bytes, bounded to keep V8 heap + GPU texture
 * memory under the renderer cap.
 *
 * Formula: `clamp(deviceMemory × 0.25 GB, 1.5 GB, 3 GB)`. Sized so a typical
 * 4-8 series study (~400 MB/volume) fits with headroom for React + V8.
 * The old `max(512 MB, deviceMemory × 0.35 GB)` could reach 5.6 GB on a
 * 16 GB machine, which fights V8's ~4 GB renderer heap and exhausted GPU
 * VRAM under chip-tab churn — see HangingProtocolGrid volume LRU.
 *
 * `deviceMemory` is privacy-rounded (0.25, 0.5, 1, 2, 4, 8) and capped at
 * 8 by the Device Memory spec, so any ≥ 8 GB machine reports 8.
 *
 * CROSS-BROWSER DETERMINISM: `navigator.deviceMemory` is Chrome-only — Safari
 * and Firefox return undefined, which used to fall straight to `4` and give
 * those browsers a smaller cache (1.5 GB floor) than Chrome (2 GB) on the SAME
 * machine, so eviction/reload behavior diverged per browser. When deviceMemory
 * is absent we now fall back to a cross-browser capability proxy
 * (`hardwareConcurrency`): a high-core machine is treated as 8 GB-class so it
 * gets the same budget it would in Chrome; a low-core one stays conservative
 * (4 → the 1.5 GB floor). Same hardware → same cache, every browser.
 */
export function computeCacheSizeBytes(
  deviceMemoryGB: number | undefined,
  hardwareConcurrency?: number | undefined,
): number {
  const dm = deviceMemoryGB ?? ((hardwareConcurrency ?? 4) >= 8 ? 8 : 4);
  const scaled = Math.round(dm * 0.25 * 1024 ** 3);
  return Math.max(1536 * 1024 ** 2, Math.min(scaled, 3 * 1024 ** 3));
}

/**
 * Web-worker count for cornerstone-dicom-image-loader.
 * Formula: `hc <= 8 ? min(hc, 3) : min(hc, 16)`. OHIF caps
 * `maxNumberOfWebWorkers` at 3 globally with the source comment
 * "some windows systems have issues with more than 3 web workers";
 * we apply that empirical cap on low-core boxes and preserve the
 * up-to-16 cap on radiology workstations.
 */
export function computeWorkerCount(hardwareConcurrency: number | undefined): number {
  const hc = hardwareConcurrency ?? 4;
  return hc <= 8 ? Math.min(hc, 3) : Math.min(hc, 16);
}

/**
 * Reinterpret an unsigned pixel buffer as signed Int16 when the DICOM declares
 * PixelRepresentation=1 but the codec returned Uint16Array. Without this, negative
 * HU values wrap to >=32768 and the image renders inverted.
 *
 * Gating is metadata-driven: PixelRepresentation, BitsAllocated, BitsStored, and
 * TransferSyntaxUID. We do not peek at pixel values to guess "is it signed?" —
 * that heuristic produced per-slice variability when a series mixed transfer
 * syntaxes (e.g. .57 + .90 in the same series), because different codecs return
 * different typed-array types for the same source bytes.
 *
 * Returns true if reinterpretation was applied, false if skipped.
 */
export type VoxelManagerFactory = (opts: {
  scalarData: ArrayBufferView;
  width: number;
  height: number;
  numberOfComponents: number;
}) => unknown;

export function applySignedPixelCorrection(
  image: any,
  voxelManagerFactory?: VoxelManagerFactory,
): boolean {
  const metadata = image?.data;
  if (!metadata?.string) return false;

  const ts = metadata.string("x00020010");
  if (!ts || !SIGN_CORRECTION_TRANSFER_SYNTAXES.includes(ts)) return false;

  const pixelRep = metadata.uint16?.("x00280103");
  if (pixelRep !== 1) return false;

  // 12-bit-stored-in-16 (BitsStored=12, common on some CT vendors) needs
  // sign-extension after the reinterpret; the raw bit-pattern reuse below
  // would leave the high 4 bits wrong. Out of scope here — file separately.
  const bitsAllocated = metadata.uint16?.("x00280100");
  const bitsStored = metadata.uint16?.("x00280101");
  if (bitsAllocated !== 16 || bitsStored !== 16) return false;

  const pixelData = image.getPixelData?.();
  if (!pixelData) return false;

  // Pre-prescale: codec returned Uint16Array and cornerstone-dicom-image-loader's
  // worker hasn't applied modality LUT yet. Zero-copy reinterpret as Int16Array.
  if (pixelData instanceof Uint16Array && image.preScale?.scaled !== true) {
    return reinterpretAsInt16(image, resolveFactory(voxelManagerFactory), ts);
  }

  // Post-prescale: worker already applied modality LUT to a Uint16 source whose
  // sign-extension step was a no-op (writing a negative back into Uint16 wraps).
  // The post-scale typed array depends on the resulting min/max range (see
  // getPixelDataTypeFromMinMax): a non-trivial intercept widens it to Uint32 or
  // Float32 (issue #310, HTJ2K Lossless RPCL + intercept=+31744 → all-white),
  // but an IDENTITY rescale (slope 1 / intercept 0) leaves the wrapped values in
  // [0, 65535] so the array stays Uint16 — the case that fell through here and
  // rendered air white on the STACK image path (the volume builder reinterprets
  // signedness itself, so the volume path was unaffected). We can't undo the
  // cast, so correct in the HU domain regardless of the post-scale array width:
  // any pixel >= slope*32768 + intercept came from a raw value >= 32768 (i.e.
  // signed-negative) — physically impossible as genuine HU — so subtract
  // slope*65536. Pixels below the threshold (genuine HU) are left untouched, so
  // a post-prescale Uint16 image that's truly all-positive is a no-op on values
  // (only re-stamped signed). Gating stays metadata-driven (PixelRep=1 etc.) —
  // we never peek at values to DECIDE whether to correct, only to apply the
  // per-pixel wrap, so a mixed-transfer-syntax series stays slice-consistent.
  if (
    image.preScale?.scaled === true &&
    (pixelData instanceof Uint32Array ||
      pixelData instanceof Float32Array ||
      pixelData instanceof Uint16Array)
  ) {
    return correctPrescaledWraparound(image, resolveFactory(voxelManagerFactory), ts);
  }

  return false;
}

function resolveFactory(override?: VoxelManagerFactory): VoxelManagerFactory {
  const factory = override ?? cornerstone?.utilities?.VoxelManager?.createImageVoxelManager;
  if (!factory) {
    throw new Error(
      "applySignedPixelCorrection: cornerstone VoxelManager unavailable. " +
        "This is a programming error — function must be called after initializeCornerstone().",
    );
  }
  return factory as VoxelManagerFactory;
}

function reinterpretAsInt16(image: any, factory: VoxelManagerFactory, ts: string): boolean {
  const pixelData = image.getPixelData();
  // Zero-copy reinterpretation: bit pattern 64536 in Uint16 is -1000 in Int16.
  const signedData = new Int16Array(pixelData.buffer, pixelData.byteOffset, pixelData.length);

  let minVal = Infinity;
  let maxVal = -Infinity;
  for (let i = 0; i < signedData.length; i++) {
    if (signedData[i] < minVal) minVal = signedData[i];
    if (signedData[i] > maxVal) maxVal = signedData[i];
  }

  stampSignedMetadata(image, signedData, minVal, maxVal, "Int16Array");
  image.voxelManager = factory({
    scalarData: signedData,
    width: image.columns ?? image.imageFrame?.columns,
    height: image.rows ?? image.imageFrame?.rows,
    numberOfComponents: 1,
  });

  recordSignCorrectionApplied(ts);
  return true;
}

function correctPrescaledWraparound(image: any, factory: VoxelManagerFactory, ts: string): boolean {
  const source = image.getPixelData();
  const slope = typeof image.slope === "number" ? image.slope : 1;
  const intercept = typeof image.intercept === "number" ? image.intercept : 0;
  // Slope is always positive for clinically meaningful modality LUTs (CT/MR/etc).
  // Bail rather than guess what a negative slope would mean.
  if (slope <= 0) return false;

  const threshold = slope * 32768 + intercept;
  const wraparound = slope * 65536;

  const corrected = new Float32Array(source.length);
  let minVal = Infinity;
  let maxVal = -Infinity;
  for (let i = 0; i < source.length; i++) {
    let v = source[i];
    if (v >= threshold) v -= wraparound;
    corrected[i] = v;
    if (v < minVal) minVal = v;
    if (v > maxVal) maxVal = v;
  }

  stampSignedMetadata(image, corrected, minVal, maxVal, "Float32Array");
  image.voxelManager = factory({
    scalarData: corrected,
    width: image.columns ?? image.imageFrame?.columns,
    height: image.rows ?? image.imageFrame?.rows,
    numberOfComponents: 1,
  });

  recordSignCorrectionApplied(ts);
  return true;
}

function stampSignedMetadata(
  image: any,
  pixelData: ArrayBufferView,
  minVal: number,
  maxVal: number,
  dataType: "Int16Array" | "Float32Array",
): void {
  image._pixelData = pixelData;
  image.pixelData = pixelData;
  image.getPixelData = () => pixelData;
  image.minPixelValue = minVal;
  image.maxPixelValue = maxVal;
  if (image.imageFrame) {
    image.imageFrame.pixelData = pixelData;
    image.imageFrame.pixelRepresentation = 1;
    image.imageFrame.signed = true;
    image.imageFrame.minPixelValue = minVal;
    image.imageFrame.maxPixelValue = maxVal;
    if (image.imageFrame.smallestPixelValue !== undefined) {
      image.imageFrame.smallestPixelValue = minVal;
    }
    if (image.imageFrame.largestPixelValue !== undefined) {
      image.imageFrame.largestPixelValue = maxVal;
    }
  }
  image.pixelRepresentation = 1;
  image.signed = true;
  image.dataType = dataType;
  if (image.imageInfo) {
    image.imageInfo.signed = true;
  }
}

/**
 * Register a custom image loader that wraps the DICOM loader with HU clamping
 * This ensures padding pixels are clamped BEFORE rendering/caching
 * Also tracks frame load timing for performance analysis.
 */
function registerClampingImageLoader(): void {
  if (!cornerstone || !cornerstoneDICOMImageLoader) {
    throw new Error("Cornerstone modules not loaded");
  }

  // Register custom loader that wraps the DICOM loader
  cornerstone.imageLoader.registerImageLoader("wadouri", (imageId: string) => {
    // Use the DICOM image loader to load the image
    const loadObject = cornerstoneDICOMImageLoader!.wadouri.loadImage(imageId);

    // Wrap the promise to apply corrections after load but before cache
    const wrappedPromise = loadObject.promise.then((image: any) => {
      // Reinterpret unsigned pixel buffer as Int16 when the DICOM is signed
      // and the codec returned Uint16Array (gated on metadata; see fn docstring)
      if (!(image as any)._signCorrected) {
        applySignedPixelCorrection(image);
        (image as any)._signCorrected = true;
      }

      // Apply HU clamping (fixes out-of-field padding pixels)
      if (!(image as any)._huClamped) {
        try {
          clampPixelDataToHURange(image);
          (image as any)._huClamped = true;
        } catch {
          // Continue with unclamped data
        }
      }
      return image;
    });

    // Return the load object with the wrapped promise
    return {
      promise: wrappedPromise,
      cancelFn: loadObject.cancelFn,
      decache: loadObject.decache,
    };
  });
}

/**
 * Initialize Cornerstone3D and DICOM image loader
 *
 * Uses a promise-based lock to prevent race conditions when multiple
 * components call initializeCornerstone simultaneously. Only the first caller
 * performs initialization; subsequent callers await the same promise.
 *
 * This function also lazily loads all Cornerstone modules to avoid WebGL
 * initialization at application startup.
 */
export async function initializeCornerstone(): Promise<void> {
  // Already initialized - fast path
  if (isInitialized) {
    return;
  }

  // Another caller is already initializing - wait for that to complete
  if (initializationPromise) {
    return initializationPromise;
  }

  // First caller - create the initialization promise
  // This MUST be synchronous assignment before any await to prevent race conditions
  initializationPromise = (async () => {
    try {
      // Dynamically import Cornerstone modules - this defers WebGL initialization
      // until this function is actually called
      const [
        cornerstoneModule,
        cornerstoneToolsModule,
        cornerstoneDICOMImageLoaderModule,
        polySegModule,
      ] = await Promise.all([
        import("@cornerstonejs/core"),
        import("@cornerstonejs/tools"),
        import("@cornerstonejs/dicom-image-loader"),
        import("@cornerstonejs/polymorphic-segmentation"),
      ]);

      // Store modules in module-scoped variables for use by other functions
      cornerstone = cornerstoneModule;
      cornerstoneTools = cornerstoneToolsModule;
      cornerstoneDICOMImageLoader = cornerstoneDICOMImageLoaderModule.default;

      // Initialize Cornerstone3D
      cornerstone.init();

      // navigator.deviceMemory is a standard Web API (Device Memory spec)
      // but isn't in TS DOM lib types yet.
      const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
      const cacheBytes = computeCacheSizeBytes(deviceMemory, navigator.hardwareConcurrency);
      const workers = computeWorkerCount(navigator.hardwareConcurrency);

      // Stable-id startup log so we can diagnose OOM reports without
      // having to guess what the user's browser reported. Bare console.info
      // (not logger.info) so this fires in prod — see logger.ts.
      console.info("[cornerstone-init] cornerstone_init_sized", {
        deviceMemory: deviceMemory ?? null,
        hardwareConcurrency: navigator.hardwareConcurrency ?? null,
        cacheBytes,
        workers,
      });

      // See computeCacheSizeBytes for the deviceMemory-scaled sizing rationale.
      cornerstone.cache.setMaxCacheSize(cacheBytes);

      // Initialize DICOM image loader
      // In Cornerstone3D 4.x, init() replaces configure() + webWorkerManager.initialize()
      // Workers are managed centrally by @cornerstonejs/core's getWebWorkerManager()
      // NOTE: Workers are MANDATORY in 4.x - non-worker decoders were removed
      // NOTE: Prescaling (rescale slope/intercept) is now applied AUTOMATICALLY
      // See computeWorkerCount for the OHIF-matched scaling rationale.
      cornerstoneDICOMImageLoader.init({
        maxWebWorkers: workers,
      });

      // Normalize raw-file VOI before the loader or a viewport creates a LUT.
      // Keep the original provider's priority and all other metadata unchanged.
      const dicomProvider = cornerstoneDICOMImageLoader.wadouri.metaData.metaDataProvider;
      cornerstone.metaData.removeProvider(dicomProvider);
      cornerstone.metaData.addProvider((type, imageId) => {
        const metadata = dicomProvider(type, imageId);
        return type === "voiLutModule" && metadata ? normalizeVoiLutMetadata(metadata) : metadata;
      });

      // Volume progressive frame loading via cornerstone's interleaved stages:
      // 3-frame preview (middle / first / last), then decimated fills, then
      // full. Nearby-frame replication keeps the viewport rendering while real
      // frames stream in. Stack viewports route via cornerstone STACK and load
      // one image at a time — no separate progressive config needed.
      cornerstone.utilities.imageRetrieveMetadataProvider.add(
        "volume",
        cornerstone.ProgressiveRetrieveImages.interleavedRetrieveStages,
      );

      // Provider order is cornerstone's default: wadouri wins on streamed
      // slices, genericMetadataProvider (seeded by
      // registerImagePlaneMetadataForSeries) fills IPP/IOP for un-streamed
      // ones — so PixelSpacing stays authoritative from the DICOM bytes.

      // Register PolySeg before tool setup. The lasso creates Contour
      // representations while saved/model masks are Labelmaps; without this
      // addon Cornerstone drops the conversion needed for a subsequent bump
      // edit and repeatedly reports that no contour representation is active.
      // The add-on and tools packages are pinned to the same release.
      // Their declaration files nevertheless name two structurally-incompatible
      // viewport interfaces, so the runtime-supported add-on needs this narrow
      // boundary cast.
      cornerstoneTools.init({ addons: { polySeg: polySegModule as any } });

      // Add navigation and interaction tools to library. AnatomicScroll is
      // our wheel/middle-drag tool — extends cornerstone's StackScrollTool
      // (referenced via cornerstoneTools.StackScrollTool inside the factory)
      // but the parent itself isn't added to any tool group.
      const AnatomicScrollToolClass = createAnatomicScrollToolClass(cornerstone, cornerstoneTools);
      cornerstoneTools.addTool(AnatomicScrollToolClass);
      cornerstoneTools.addTool(cornerstoneTools.WindowLevelTool);
      cornerstoneTools.addTool(cornerstoneTools.PanTool);
      cornerstoneTools.addTool(cornerstoneTools.ZoomTool);

      // Add measurement tools
      cornerstoneTools.addTool(cornerstoneTools.LengthTool);
      cornerstoneTools.addTool(cornerstoneTools.RectangleROITool);

      // Add segmentation tool (freehand contour with interpolation)
      cornerstoneTools.addTool(createSegmentationLassoToolClass(cornerstoneTools));

      // Add bump contour refinement tool (custom sculpting brush)
      const BumpContourToolClass = createBumpContourToolClass(cornerstone, cornerstoneTools);
      cornerstoneTools.addTool(BumpContourToolClass);

      // Add region grow tool (one-click flood-fill segmentation, V2 S3)
      const RegionGrowToolClass = createRegionGrowToolClass(cornerstone, cornerstoneTools);
      cornerstoneTools.addTool(RegionGrowToolClass);

      // Add measure-click tool (📐 case-viewer flow → /api/measure/lesion-segment)
      const MeasureClickToolClass = createMeasureClickToolClass(cornerstone, cornerstoneTools);
      cornerstoneTools.addTool(MeasureClickToolClass);

      // Add crosshairs for MPR synchronization
      cornerstoneTools.addTool(cornerstoneTools.CrosshairsTool);

      // Add reference-lines: three plane-specific subclasses so axial/coronal/
      // sagittal source intersections each get their own color. Configured +
      // toggled by useReferenceLinesSync at the HangingProtocolGrid level.
      const planeRefLineClasses = createPlaneReferenceLineToolClasses(cornerstoneTools);
      cornerstoneTools.addTool(planeRefLineClasses.ReferenceLinesAxial);
      cornerstoneTools.addTool(planeRefLineClasses.ReferenceLinesCoronal);
      cornerstoneTools.addTool(planeRefLineClasses.ReferenceLinesSagittal);

      // Concurrency caps per request type. HTTP/2 multiplexing means we can
      // push these high without TCP-level penalty. Thumbnail is wide because
      // interleavedRetrieveStages drives volume progressive via Thumbnail
      // priority (see ProgressiveRetrieveImages.interleavedRetrieveStages).
      //
      // Raised from 16 -> 48 (Prefetch 8 -> 16). Cold-cache CDP A/B on a real
      // staging study (549-frame default hanging view) measured full-volume
      // load 20.4s -> 8.6s (2.38x) and time-to-first-image ~2.2s with no
      // decode/throughput regression; 48 was the throughput sweet spot (64 was
      // marginally worse). Load is latency-bound (requests x RTT / concurrency),
      // so more in-flight requests is the dominant first-paint lever.
      const { RequestType } = cornerstone.Enums;
      const poolConfig = {
        [RequestType.Interaction]: 48,
        [RequestType.Thumbnail]: 48,
        [RequestType.Prefetch]: 16,
        [RequestType.Compute]: 4,
      };

      const imageRetrievalPool = cornerstone.imageRetrievalPoolManager;
      if (imageRetrievalPool?.maxNumRequests) {
        imageRetrievalPool.maxNumRequests = poolConfig;
      }

      const imageLoadPool = cornerstone.imageLoadPoolManager;
      if (imageLoadPool?.maxNumRequests) {
        imageLoadPool.maxNumRequests = poolConfig;
      }

      // Register custom image loader AFTER all initialization
      // This wraps the DICOM loader and applies HU clamping
      registerClampingImageLoader();

      // Progressive low-res-first loading (flag-gated: ?progressive=1). Dynamic
      // import keeps the preview/openjph code out of the bundle unless enabled.
      if (isProgressiveEnabled()) {
        import("./rangedProgressiveLoader")
          .then((m) => m.installProgressiveDevHook())
          .catch((e) => logger.warn("[progressive] dev hook install failed", { error: String(e) }));

        // Register the low-res volume image loader ("lowresprev:" scheme) so
        // SessionVolumeMount can build+display a low-res companion volume first.
        import("./lowResVolume")
          .then((m) => m.registerLowResImageLoader(cornerstone!, cornerstoneDICOMImageLoader!))
          .catch((e) =>
            logger.warn("[progressive] low-res loader install failed", { error: String(e) }),
          );

        // Time-to-first-image: IMAGE_RENDERED fires on the viewport ELEMENT (not
        // the global target). Catch ELEMENT_ENABLED on eventTarget to get each
        // element, then mark the first render (measured from study-load-start).
        const { IMAGE_RENDERED, ELEMENT_ENABLED } = cornerstone.Enums.Events;
        const markFirstImage = () => {
          if ((window as { __firstImageMs?: number }).__firstImageMs != null) return;
          const ms = perfMeasure("first-image", "study-load-start");
          (window as { __firstImageMs?: number }).__firstImageMs = Math.round(ms);
          console.info("[progressive] first-image", Math.round(ms), "ms");
        };
        cornerstone.eventTarget.addEventListener(ELEMENT_ENABLED, (e: Event) => {
          const el = (e as CustomEvent).detail?.element as HTMLElement | undefined;
          el?.addEventListener(IMAGE_RENDERED, markFirstImage, { once: true });
        });
      }

      // Suppress noisy unhandled promise rejections from DICOM frame fetch failures
      // These are retried by Cornerstone internally — the console noise is misleading
      window.addEventListener("unhandledrejection", (event) => {
        const reason = event.reason;
        if (
          reason &&
          typeof reason === "object" &&
          (reason instanceof XMLHttpRequest ||
            reason?.error instanceof XMLHttpRequest ||
            (reason?.message && reason.message.includes("dicom")))
        ) {
          event.preventDefault();
        }
      });

      isInitialized = true;
    } catch (error) {
      // Reset promise so initialization can be retried
      initializationPromise = null;
      throw error;
    }
  })();

  // Return the promise so callers can await it
  return initializationPromise;
}

/**
 * Get the Cornerstone module - throws if not initialized
 * This is used by components that need direct access to Cornerstone
 */
export function getCornerstone(): typeof import("@cornerstonejs/core") {
  if (!cornerstone) {
    throw new Error("Cornerstone not initialized. Call initializeCornerstone() first.");
  }
  return cornerstone;
}

/**
 * Get the CornerstoneTools module - throws if not initialized
 */
export function getCornerstoneTools(): typeof import("@cornerstonejs/tools") {
  if (!cornerstoneTools) {
    throw new Error("CornerstoneTools not initialized. Call initializeCornerstone() first.");
  }
  return cornerstoneTools;
}

// String-literal-duplicated with utils/renderingEngine.SHARED_RENDERING_ENGINE_ID
// to avoid a circular import (renderingEngine imports getCornerstone from here).
const _SHARED_ENGINE_ID = "viewerSharedEngine";

/** Find the Cornerstone viewport for a hanging protocol slot by prefix match. */
export function findHangingProtocolViewport(slotId: string): any | null {
  try {
    const cs = getCornerstone();
    const engine = cs.getRenderingEngine(_SHARED_ENGINE_ID);
    if (!engine) return null;
    const prefix = `hangingProtocol_${slotId}`;
    return (engine.getViewports() as any[]).find((v: any) => v.id?.startsWith(prefix)) ?? null;
  } catch {
    return null;
  }
}

/**
 * Find a Cornerstone viewport that is currently displaying a given series UID.
 * Checks sibling viewports first (if provided), then falls back to scanning
 * all rendering engines. Returns { viewport, element } or null.
 */
export function findViewportForSeries(
  seriesUid: string,
  siblings?: Record<string, { seriesUid?: string | null; viewport?: any }> | null,
): { viewport: any; element: HTMLElement } | null {
  const baseUid = seriesUid.split("::")[0];

  // Check siblings first (fast path)
  if (siblings) {
    for (const sib of Object.values(siblings)) {
      if (sib.seriesUid?.split("::")[0] === baseUid && sib.viewport) {
        const el = sib.viewport.element ?? null;
        if (el) return { viewport: sib.viewport, element: el };
      }
    }
  }

  // Fallback: scan all rendering engines
  try {
    const cs = getCornerstone();
    for (const engine of cs.getRenderingEngines?.() ?? []) {
      for (const vp of (engine.getViewports?.() ?? []) as any[]) {
        const ids: string[] = vp.getImageIds?.() ?? [];
        if (
          ids.some(
            (id: string) =>
              cs.metaData.get("generalSeriesModule", id)?.seriesInstanceUID === baseUid ||
              id.includes(baseUid),
          )
        ) {
          const el: HTMLElement | null = vp.element ?? null;
          if (el) return { viewport: vp, element: el };
        }
      }
    }
  } catch {
    // Cornerstone not ready
  }
  return null;
}

/**
 * Load DICOM files from local directory
 * @param files Array of File objects
 * @returns Array of Cornerstone image IDs
 */
export async function loadLocalDicomFiles(files: File[]): Promise<string[]> {
  if (!isInitialized) {
    await initializeCornerstone();
  }

  if (!cornerstoneDICOMImageLoader) {
    throw new Error("DICOM image loader not initialized");
  }

  const imageIds: string[] = [];

  for (const file of files) {
    try {
      // Create a blob URL for the file
      const imageId = cornerstoneDICOMImageLoader.wadouri.fileManager.add(file);
      imageIds.push(imageId);
    } catch {
      // Failed to load DICOM file - skip it
    }
  }

  // Sort by DICOM instance number for correct slice order
  return await sortImageIdsByInstanceNumber(imageIds);
}

/**
 * Load DICOM files from URLs (for backend integration)
 * @param urls Array of DICOM file URLs (from backend API)
 * @param apiBaseUrl The base URL for the API (for proxying)
 * @returns Array of Cornerstone image IDs sorted by instance number
 */
export async function createImageIdsFromUrls(
  urls: string[],
  apiBaseUrl?: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<string[]> {
  if (!isInitialized) {
    throw new Error("Cornerstone not initialized. Call initializeCornerstone() first.");
  }

  const imageIds = urls.map((url) => {
    // If apiBaseUrl is provided, create proxy URL
    if (apiBaseUrl) {
      const proxyUrl = `${apiBaseUrl}/proxy_dicom?url=${encodeURIComponent(url)}`;
      return `wadouri:${proxyUrl}`;
    }
    // Otherwise, use the URL directly
    return `wadouri:${url}`;
  });

  // Sort by DICOM instance number for correct slice order
  // This loads all images to read metadata, but ensures correct order
  return await sortImageIdsByInstanceNumber(imageIds, onProgress);
}

/**
 * Extract instance number from DICOM image metadata
 * @param imageId Cornerstone image ID
 * @returns Instance number or null if not available
 */
export async function getInstanceNumber(imageId: string): Promise<number | null> {
  try {
    const image = await loadAndCacheImageWithClamping(imageId);
    // Try to get InstanceNumber from metadata
    const metadata = (image as any).data;
    if (metadata && metadata.string) {
      const instanceNumber = metadata.string("x00200013"); // InstanceNumber tag
      if (instanceNumber) {
        return parseInt(instanceNumber, 10);
      }
    }
    // Fallback to imagePositionPatient Z coordinate if available
    if (
      (image as any).imagePositionPatient &&
      (image as any).imagePositionPatient[2] !== undefined
    ) {
      return (image as any).imagePositionPatient[2];
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Sort image IDs by DICOM instance number
 * @param imageIds Array of Cornerstone image IDs
 * @param onProgress Optional callback for progress updates
 * @returns Sorted array of image IDs
 */
async function sortImageIdsByInstanceNumber(
  imageIds: string[],
  onProgress?: (loaded: number, total: number) => void,
): Promise<string[]> {
  const total = imageIds.length;
  let loaded = 0;

  // Load metadata for all images with progress tracking
  const imageMetadata = await Promise.all(
    imageIds.map(async (imageId) => {
      const instanceNumber = await getInstanceNumber(imageId);
      loaded++;
      if (onProgress) {
        onProgress(loaded, total);
      }
      return {
        imageId,
        instanceNumber,
      };
    }),
  );

  // Sort by instance number, falling back to original order for images without metadata
  imageMetadata.sort((a, b) => {
    if (a.instanceNumber !== null && b.instanceNumber !== null) {
      return a.instanceNumber - b.instanceNumber;
    }
    if (a.instanceNumber !== null) return -1;
    if (b.instanceNumber !== null) return 1;
    // If neither has instance number, maintain original order
    return imageIds.indexOf(a.imageId) - imageIds.indexOf(b.imageId);
  });

  return imageMetadata.map((item) => item.imageId);
}

export interface ImageLoadResult {
  imageId: string;
  image: any;
  instanceNumber: number | null;
  error?: Error;
}

/**
 * Load images with controlled concurrency to avoid overwhelming the browser/network
 * @param imageIds Array of Cornerstone image IDs to load
 * @param concurrency Maximum concurrent requests (default: 15 for HTTP/2 multiplexing)
 * @param onProgress Progress callback (loaded, total)
 * @param signal AbortSignal for cancellation
 * @param onImageLoaded Optional callback fired when each individual image loads (for progressive display)
 * @returns Array of load results with images and metadata
 */
export async function loadImagesWithConcurrency(
  imageIds: string[],
  concurrency: number = 15,
  onProgress?: (loaded: number, total: number) => void,
  signal?: AbortSignal,
  onImageLoaded?: (result: ImageLoadResult, index: number) => void,
): Promise<ImageLoadResult[]> {
  const results: ImageLoadResult[] = [];
  const total = imageIds.length;
  let loaded = 0;

  // Timing stats for performance analysis
  const loadTimes: number[] = [];
  const overallStartTime = performance.now();
  let lastLogTime = overallStartTime;
  const LOG_INTERVAL = 5000; // Log stats every 5 seconds

  // Create a queue of [imageId, originalIndex] pairs to process
  const queue: Array<[string, number]> = imageIds.map((id, idx) => [id, idx]);
  const activeLoads = new Map<Promise<void>, boolean>();

  // Process queue with controlled concurrency
  while (queue.length > 0 || activeLoads.size > 0) {
    // Check for cancellation
    if (signal?.aborted) {
      throw new Error("Loading cancelled");
    }

    // Start new loads up to concurrency limit
    while (activeLoads.size < concurrency && queue.length > 0) {
      const [imageId, originalIndex] = queue.shift()!;

      const loadTask = (async () => {
        const loadStartTime = performance.now();
        let result: ImageLoadResult;
        try {
          // Load image and extract instance number
          const image = await loadAndCacheImageWithClamping(imageId);

          // Track load time for this image
          const loadTime = performance.now() - loadStartTime;
          loadTimes.push(loadTime);

          // Extract instance number from metadata
          const metadata = (image as any).data;
          let instanceNumber: number | null = null;
          if (metadata && metadata.string) {
            const instanceNumberStr = metadata.string("x00200013");
            if (instanceNumberStr) {
              instanceNumber = parseInt(instanceNumberStr, 10);
            }
          }

          result = { imageId, image, instanceNumber };
          results.push(result);
        } catch (error) {
          const loadTime = performance.now() - loadStartTime;
          loadTimes.push(loadTime);
          result = {
            imageId,
            image: null,
            instanceNumber: null,
            error: error as Error,
          };
          results.push(result);
        } finally {
          loaded++;
          if (onProgress) {
            onProgress(loaded, total);
          }
          // Fire progressive loading callback with original index
          if (onImageLoaded) {
            onImageLoaded(result!, originalIndex);
          }

          // Log timing stats periodically
          const now = performance.now();
          if (now - lastLogTime > LOG_INTERVAL && loadTimes.length > 0) {
            const avgTime = loadTimes.reduce((a, b) => a + b, 0) / loadTimes.length;
            const minTime = Math.min(...loadTimes);
            const maxTime = Math.max(...loadTimes);
            const elapsed = (now - overallStartTime) / 1000;
            const throughput = loaded / elapsed;
            logger.debug(
              `[PERF] loadImagesWithConcurrency: ${loaded}/${total} loaded, ` +
                `concurrency=${activeLoads.size}, ` +
                `avg=${avgTime.toFixed(0)}ms, min=${minTime.toFixed(0)}ms, max=${maxTime.toFixed(0)}ms, ` +
                `throughput=${throughput.toFixed(1)} img/s`,
            );
            lastLogTime = now;
          }
        }
      })();

      // Track this load and set up completion handler
      activeLoads.set(loadTask, false);
      loadTask
        .then(() => {
          activeLoads.delete(loadTask);
        })
        .catch(() => {
          activeLoads.delete(loadTask);
        });
    }

    // Wait for at least one load to complete
    if (activeLoads.size > 0) {
      await Promise.race(Array.from(activeLoads.keys()));
    }
  }

  // Final timing summary
  if (loadTimes.length > 0) {
    const totalTime = performance.now() - overallStartTime;
    const avgTime = loadTimes.reduce((a, b) => a + b, 0) / loadTimes.length;
    const minTime = Math.min(...loadTimes);
    const maxTime = Math.max(...loadTimes);
    const throughput = total / (totalTime / 1000);
    logger.debug(
      `[PERF] loadImagesWithConcurrency COMPLETE: ${total} images in ${totalTime.toFixed(0)}ms ` +
        `(${throughput.toFixed(1)} img/s), per-image: avg=${avgTime.toFixed(0)}ms, min=${minTime.toFixed(0)}ms, max=${maxTime.toFixed(0)}ms`,
    );
  }

  return results;
}

/**
 * Sort image load results by DICOM instance number
 * @param results Array of image load results
 * @returns Sorted array of image IDs
 */
export function sortResultsByInstanceNumber(results: ImageLoadResult[]): string[] {
  const sortedResults = [...results].sort((a, b) => {
    if (a.instanceNumber !== null && b.instanceNumber !== null) {
      return a.instanceNumber - b.instanceNumber;
    }
    if (a.instanceNumber !== null) return -1;
    if (b.instanceNumber !== null) return 1;
    return 0;
  });

  return sortedResults.map((result) => result.imageId);
}

/**
 * Determine the safe minimum value for a typed array to ensure black rendering
 * Prevents wraparound in unsigned arrays by using 0 instead of negative values
 * @param typedArray The typed array constructor or instance
 * @param targetValue The desired value in array units (not HU)
 * @returns Safe value that won't wrap around and will render as black
 */
function getSafeClampValue(typedArray: any, targetValue: number): number {
  // Handle both constructor functions and instances
  let constructorName: string;

  if (typedArray && typeof typedArray === "function") {
    // It's a constructor function
    constructorName = typedArray.name;
  } else if (typedArray && typedArray.constructor) {
    // It's an instance
    constructorName = typedArray.constructor.name;
  } else {
    // Unknown type, use safe default
    return 0;
  }

  switch (constructorName) {
    case "Uint8Array":
    case "Uint8ClampedArray":
    case "Uint16Array":
    case "Uint32Array":
      // For unsigned arrays, use 0 to avoid wraparound
      // This converts to intercept HU (typically -1024 for CT) → BLACK
      return 0;

    case "Int8Array":
      // Range: -128 to 127
      return Math.max(targetValue, -128);

    case "Int16Array":
      // Range: -32768 to 32767
      return Math.max(targetValue, -32768);

    case "Int32Array":
      // Range: -2147483648 to 2147483647
      return Math.max(targetValue, -2147483648);

    case "Float32Array":
    case "Float64Array":
      // No range limits for floats
      return targetValue;

    default:
      // Fallback: use 0 for safety
      return 0;
  }
}

/**
 * Clamp pixel data to valid HU range to handle out-of-field padding pixels
 * @param image Cornerstone image object
 * @param minHU Minimum valid HU value (default: -1024, air density)
 * @param maxHU Maximum valid HU value (default: 5000, above bone)
 *
 * IMPORTANT: This function creates a NEW clamped pixel array and overrides getPixelData()
 */
export function clampPixelDataToHURange(
  image: any,
  minHU: number = -1024,
  maxHU: number = 5000,
): void {
  const originalPixelData = image.getPixelData();
  const slope = image.slope || 1;
  const intercept = image.intercept || 0;

  // Check if the image data is already prescaled (pixel values are in HU units)
  // preScale can be a boolean or an object with a 'scaled' property
  const isPreScaled = image.preScale === true || image.preScale?.scaled === true;

  // Create a NEW typed array of the same type
  const TypedArrayConstructor = originalPixelData.constructor;
  const clampedPixelData = new TypedArrayConstructor(originalPixelData.length);

  // Clamp value for out-of-field padding: use extremely low value to ensure black rendering
  // Use -3000 HU (well below any anatomical structure) to guarantee black pixels
  const paddingClampHU = -3000;

  // Per-pixel range check below reads minHU/maxHU directly; the only branch-
  // dependent value is clampValue (the padding value to write when a pixel
  // falls outside the HU range).
  const clampValue: number = isPreScaled
    ? getSafeClampValue(TypedArrayConstructor, paddingClampHU)
    : getSafeClampValue(TypedArrayConstructor, (paddingClampHU - intercept) / slope);

  // Copy pixel data and clamp out-of-range values
  for (let i = 0; i < originalPixelData.length; i++) {
    const pixelValue = originalPixelData[i];

    // For prescaled data, pixelValue IS the HU value
    const valueToCheck = isPreScaled ? pixelValue : pixelValue * slope + intercept;

    // If outside valid HU range, clamp to extremely low value (renders as black)
    if (valueToCheck < minHU || valueToCheck > maxHU) {
      clampedPixelData[i] = clampValue;
    } else {
      clampedPixelData[i] = pixelValue;
    }
  }

  // CRITICAL: Override getPixelData() to always return the clamped array
  image.getPixelData = function () {
    return clampedPixelData;
  };

  // Also set pixelData property directly in case Cornerstone reads from it
  image.pixelData = clampedPixelData;

  // Set the pixel data on the imageFrame if it exists
  if (image.imageFrame) {
    image.imageFrame.pixelData = clampedPixelData;
  }
}

/**
 * Load and cache a DICOM image with HU range clamping applied
 * This wrapper now just calls the standard loader since clamping is handled
 * by the custom image loader registered during initialization
 * @param imageId Cornerstone image ID
 * @param enableHUClamping Whether to apply HU clamping (default: true, ignored since clamping is now automatic)
 * @returns Promise resolving to the loaded and preprocessed image
 */
export async function loadAndCacheImageWithClamping(
  imageId: string,
  _enableHUClamping: boolean = true,
): Promise<any> {
  if (!cornerstone) {
    throw new Error("Cornerstone not initialized. Call initializeCornerstone() first.");
  }
  // The custom image loader automatically applies clamping during load
  // No need for additional processing here
  return await cornerstone.imageLoader.loadAndCacheImage(imageId);
}

/**
 * Extract DICOM metadata from an image
 * @param imageId Cornerstone image ID
 * @returns DICOM metadata
 */
export async function getDicomMetadata(imageId: string): Promise<any> {
  const image = await loadAndCacheImageWithClamping(imageId);
  const metadata = (image as any).data;
  let instanceNumber: number | undefined;

  if (metadata && metadata.string) {
    const instanceNumberStr = metadata.string("x00200013");
    if (instanceNumberStr) {
      instanceNumber = parseInt(instanceNumberStr, 10);
    }
  }

  return {
    rows: image.height,
    columns: image.width,
    windowCenter: image.windowCenter,
    windowWidth: image.windowWidth,
    slope: image.slope || 1,
    intercept: image.intercept || 0,
    minPixelValue: image.minPixelValue,
    maxPixelValue: image.maxPixelValue,
    instanceNumber,
  };
}

/**
 * Purge the image cache without resetting initialization
 * Use this when switching series or to free up memory
 */
export function purgeImageCache(): void {
  if (isInitialized && cornerstone) {
    cornerstone.cache.purgeCache();
    // No separate tracking needed - clamping flag is stored on image objects
  }
}

/**
 * Release a volume's GPU texture and remove it from the cache.
 *
 * Cornerstone3D's `_decacheVolume` (called by `removeVolumeLoadObject`) handles
 * `cancelLoading`, `imageData.delete()`, `sharedCacheKey` un-pinning, and the
 * map delete — but never touches `vtkOpenGLTexture`, so the GPU texture leaks
 * on every evict and the WebGL context eventually exhausts (the white-out
 * symptom). Calling `volume.destroy()` would dispose the texture but also
 * nulls `imageData`, which `_decacheVolume` then dereferences. Release the
 * texture in-place and let `_decacheVolume` do the rest.
 */
export function disposeAndRemoveVolume(volumeId: string): boolean {
  if (!isInitialized || !cornerstone) return false;
  const cache = cornerstone.cache;
  const volume = cache.getVolume(volumeId) as
    | { vtkOpenGLTexture?: { releaseGraphicsResources?: () => void; delete?: () => void } }
    | undefined;
  if (!volume) return false;
  try {
    volume.vtkOpenGLTexture?.releaseGraphicsResources?.();
    volume.vtkOpenGLTexture?.delete?.();
  } catch (e) {
    logger.warn("[dicomLoader] volume_evict_texture_dispose_failed", {
      volumeId,
      err: String(e),
    });
  }
  try {
    cache.removeVolumeLoadObject(volumeId);
  } catch (e) {
    logger.warn("[dicomLoader] volume_evict_remove_failed", { volumeId, err: String(e) });
    return false;
  }
  return true;
}

/**
 * Extract the study UID embedded in a canonical wadouri imageId.
 *
 * `buildS3ImageIds` produces `wadouri:<cdn>/dicom/<studyUID>/<seriesUID>/<sop>.dcm`,
 * so the study UID is the path segment right after `/dicom/`. Returns null for any
 * id that doesn't carry the segment (non-canonical scheme, unexpected layout) so
 * callers never mis-attribute — and therefore never evict — such an image.
 */
export function studyUidFromImageId(imageId: string): string | null {
  const m = imageId.match(/\/dicom\/([^/]+)\//);
  return m ? m[1] : null;
}

/**
 * The cached image ids that belong to a study other than `currentStudyId`.
 *
 * `currentStudyId` may be a bare study UID or a viewer case id
 * `<studyUID>::<jobId>` (reader / study routes, `studyRowId`). Image ids only
 * carry the bare UID, so the `::<jobId>` suffix is dropped before comparing;
 * comparing the composite id evicted every current-study image (ENG-247).
 * Ids with no attributable study are never returned.
 */
export function foreignStudyImageIds(imageIds: string[], currentStudyId: string): string[] {
  const currentStudyUid = currentStudyId.split("::")[0];
  return imageIds.filter((imageId) => {
    const study = studyUidFromImageId(imageId);
    return study !== null && study !== currentStudyUid;
  });
}

/**
 * Evict every cached IMAGE that belongs to a study other than `currentStudyId`
 * (bare UID or `<studyUID>::<jobId>`, see `foreignStudyImageIds`).
 *
 * The volume LRU + `foreignStudyVolumeIds` purge only evict
 * `cornerstoneStreamingImageVolume:` VOLUMES. Non-reconstructable cor/sag
 * reformats render through a StackViewport, whose frames are plain `wadouri:`
 * images with no volume id — so nothing evicted them and they accumulated across
 * every case a reader opened. Once the image cache filled
 * (`CACHE_SIZE_EXCEEDED`), a freshly-opened recon's end frames (fetched last / on
 * scroll) could no longer be cached and stayed blank while the middle survived —
 * the "middle loads, ends stay blank, refresh fixes it" report. Purging
 * foreign-study images on each case switch bounds the image cache to the current
 * study, mirroring the volume-side fix. Current-study images are never touched,
 * so this does not re-fetch the case you are reading. Returns the count evicted.
 *
 * Reaches into `cache._imageCache` because @cornerstonejs/core exposes no public
 * "list cached image ids" accessor; guarded + logged so a future rename degrades
 * to a no-op (cache stays bounded by cornerstone's own maxCacheSize eviction)
 * rather than throwing. See renderingEngine.ts `collectOffscreenGLCanvases` for
 * the same duck-typed-with-fallback pattern.
 */
export function purgeForeignStudyImages(currentStudyId: string): number {
  if (!isInitialized || !cornerstone || !currentStudyId) return 0;
  const cache = cornerstone.cache;
  const imageCache = (cache as unknown as { _imageCache?: Map<string, unknown> })._imageCache;
  if (!imageCache || typeof imageCache.keys !== "function") {
    logger.warn("[dicomLoader] purge_foreign_images_cache_map_missing");
    return 0;
  }
  let evicted = 0;
  // Snapshot keys first — removeImageLoadObject mutates the map during iteration.
  for (const imageId of foreignStudyImageIds(Array.from(imageCache.keys()), currentStudyId)) {
    try {
      cache.removeImageLoadObject(imageId, { force: true });
      evicted++;
    } catch (e) {
      logger.warn("[dicomLoader] foreign_image_evict_failed", { imageId, err: String(e) });
    }
  }
  return evicted;
}

/**
 * Purge entire Cornerstone cache
 * Use this when navigating between studies to free memory
 * Unlike cleanup(), this doesn't reset initialization state
 */
export function purgeAllCornerstoneCache(): void {
  if (!isInitialized || !cornerstone) return;

  try {
    cornerstone.cache.purgeCache();
  } catch {
    // Cache purge failed
  }
}

/**
 * Clean up Cornerstone resources completely
 * Use this only on application teardown
 */
export function cleanup(): void {
  if (isInitialized && cornerstone) {
    cornerstone.cache.purgeCache();
    // No separate tracking needed - clamping flag is stored on image objects
    isInitialized = false;
  }
}

// Export getters for cornerstone and cornerstoneTools for backwards compatibility
// Components that previously imported { cornerstone } should now use getCornerstone()
export { getCornerstone as cornerstone, getCornerstoneTools as cornerstoneTools };

// Export Cornerstone classes/types for use after initialization
// These must be called AFTER initializeCornerstone() has completed
export function getRenderingEngine(): typeof import("@cornerstonejs/core").RenderingEngine {
  if (!cornerstone) {
    throw new Error("Cornerstone not initialized. Call initializeCornerstone() first.");
  }
  return cornerstone.RenderingEngine;
}

export function getEnums(): typeof import("@cornerstonejs/core").Enums {
  if (!cornerstone) {
    throw new Error("Cornerstone not initialized. Call initializeCornerstone() first.");
  }
  return cornerstone.Enums;
}

// Note: Types from @cornerstonejs/core is a TypeScript namespace for type definitions,
// not a runtime export. Import it directly as a type: import type { Types } from '@cornerstonejs/core'
